import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PageTitle } from "./AdminAnalytics";
import { CoveragePanel } from "./CoveragePanel";
import { NewProductsNotice } from "./NewProductsNotice";
import { Field } from "./fields";
import { ShopifySyncButton } from "./ShopifySyncButton";

export default function EntityManager({ config }) {
  const { coll, columns, fields, empty, sortable } = config;
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get(`/admin/${coll}`).then((r) => setItems(r.data)), [coll]);
  useEffect(() => {
    load();
    api.get("/admin/tags").then((r) => setTags(r.data));
    if (coll === "products") api.get("/admin/products").then((r) => setProducts(r.data));
  }, [coll, load]);

  const save = async () => {
    try {
      const { id, ...rest } = editing;
      if (id) await api.put(`/admin/${coll}/${id}`, editing);
      else await api.post(`/admin/${coll}`, { ...rest, order: sortable ? items.length + 1 : rest.order });
      toast.success("Salvato");
      setEditing(null);
      load();
    } catch (e) { toast.error(typeof errMsg(e) === "string" ? errMsg(e) : "Controlla i campi"); }
  };

  const remove = async (item) => {
    if (!window.confirm("Eliminare questo elemento?")) return;
    await api.delete(`/admin/${coll}/${item.id}`);
    load();
  };

  const patch = async (item, key, val) => {
    await api.put(`/admin/${coll}/${item.id}`, { ...item, [key]: val });
    load();
  };

  const move = async (i, dir) => {
    const list = items.map((it, idx) => ({ ...it, order: idx + 1 }));
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i].order, list[j].order] = [list[j].order, list[i].order];
    await Promise.all([api.put(`/admin/${coll}/${list[i].id}`, list[i]), api.put(`/admin/${coll}/${list[j].id}`, list[j])]);
    load();
  };

  return (
    <div data-testid={`admin-${coll}-page`}>
      <PageTitle eyebrow={config.eyebrow} title={config.title}>
        <div className="flex items-center gap-2">
          {coll === "products" && <ShopifySyncButton onDone={load} />}
          <button data-testid={`admin-add-${coll}-btn`} className="fs-btn !py-3" onClick={() => setEditing({ ...empty })}><Plus size={14} /> Aggiungi</button>
        </div>
      </PageTitle>
      <p className="text-sm text-[#8C8275] -mt-4 mb-6 max-w-2xl">{config.description}</p>
      {coll === "products" && <NewProductsNotice onSynced={load} />}
      {coll === "products" && <CoveragePanel />}
      <div className="bg-white border border-[#E2DACF] overflow-x-auto">
        <table className="w-full text-sm" data-testid={`admin-${coll}-table`}>
          <thead className="bg-[#FAF8F5]">
            <tr>
              {sortable && <th className="w-16" />}
              {columns.map(([k, label]) => <th key={k} className="text-left font-medium text-xs text-[#8C8275] px-4 py-3">{label}</th>)}
              <th className="w-24" />
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={item.id} className="border-t border-[#F2ECE4] hover:bg-[#FAF8F5]" data-testid={`admin-row-${coll}-${i}`}>
                {sortable && (
                  <td className="px-2"><div className="flex">
                    <button data-testid={`admin-move-up-${i}`} onClick={() => move(i, -1)} className="p-1 text-[#8C8275] hover:text-black"><ArrowUp size={14} /></button>
                    <button data-testid={`admin-move-down-${i}`} onClick={() => move(i, 1)} className="p-1 text-[#8C8275] hover:text-black"><ArrowDown size={14} /></button>
                  </div></td>
                )}
                {columns.map(([k, , render, type]) => (
                  <td key={k} className="px-4 py-3 align-middle">
                    {type === "toggle"
                      ? <Switch data-testid={`admin-toggle-${k}-${i}`} checked={!!item[k]} onCheckedChange={(v) => patch(item, k, v)} />
                      : render ? render(item[k], item) : String(item[k] ?? "")}
                  </td>
                ))}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`admin-edit-${coll}-${i}`} onClick={() => setEditing({ ...item })} className="p-2 text-[#595652] hover:text-black"><Pencil size={15} /></button>
                  <button data-testid={`admin-delete-${coll}-${i}`} onClick={() => remove(item)} className="p-2 text-[#595652] hover:text-[#A0482F]"><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FAF8F5]" data-testid={`admin-${coll}-dialog`}>
          <DialogHeader><DialogTitle className="font-serif text-2xl font-normal">{editing?.id ? "Modifica" : "Nuovo"} · {config.title}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-5">
              {fields.map(([key, label, type, opts, readonlyOnEdit]) => (
                <Field key={key} name={key} label={label} type={type} options={opts} tags={tags} products={products.filter((p) => p.id !== editing.id && p.step_key === editing.step_key)}
                  disabled={readonlyOnEdit && !!editing.id} value={editing[key]} onChange={(v) => setEditing((e) => ({ ...e, [key]: v }))} />
              ))}
              <div className="flex justify-end gap-3 pt-2">
                <button data-testid="admin-dialog-cancel-btn" className="fs-btn-ghost" onClick={() => setEditing(null)}>Annulla</button>
                <button data-testid="admin-dialog-save-btn" className="fs-btn !py-3" onClick={save}>Salva</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
